<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import {
  getPreChatFields,
  ensureMessageField,
  pinMessageFieldLast,
  normalizeSpaceField,
  normalizeStandardBookingField,
  isSpaceField,
  isBookingField,
} from 'dashboard/helper/preChat';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import FieldPicker from './FieldPicker.vue';
import FormCanvas from './FormCanvas.vue';
import CreateAttributeDialog from './CreateAttributeDialog.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);
const customAttributes = computed(
  () => getters['attributes/getAttributes'].value
);
const spaces = computed(() => getters['spaces/getSpaces']?.value || []);

const preChatFormEnabled = ref(false);
const preChatMessage = ref('');
const submitButtonLabel = ref('');
const requiredBeforeChat = ref(true);
const preChatFields = ref([]);
const preChatFieldsets = ref([]);

const createAttributeDialogRef = ref(null);

const preChatFieldOptions = computed(() => {
  const { pre_chat_form_options: preChatFormOptions } = props.inbox;
  return getPreChatFields({
    preChatFormOptions,
    customAttributes: customAttributes.value,
  });
});

const setDefaults = () => {
  const { pre_chat_form_enabled: enabled } = props.inbox;
  preChatFormEnabled.value = enabled;
  const {
    pre_chat_message: message,
    submit_button_label: buttonLabel,
    required_before_chat: requiredBeforeChatOption,
    pre_chat_fields: fields,
    pre_chat_fieldsets: fieldsets,
  } = preChatFieldOptions.value || {};
  preChatMessage.value = message;
  submitButtonLabel.value = buttonLabel || '';
  requiredBeforeChat.value = requiredBeforeChatOption !== false;
  preChatFields.value = pinMessageFieldLast(ensureMessageField(fields || []));
  preChatFieldsets.value = fieldsets || [];
};

watch(() => props.inbox, setDefaults);
setDefaults();

const hydrateSpaceFieldValues = async () => {
  if (!preChatFields.value.some(isSpaceField)) return;

  if (!spaces.value.length) {
    await store.dispatch('spaces/get');
  }

  const values = spaces.value.map(space => ({
    id: space.id,
    name: space.name,
    resource_id: space.resource_id,
    max_capacity_seated: space.max_capacity_seated,
    max_capacity_standing: space.max_capacity_standing,
    working_hours_enabled:
      space.working_hours_enabled ??
      space.metadata?.working_hours_enabled ??
      !!space.schedule?.days?.length,
    schedule: space.schedule,
  }));
  preChatFields.value = preChatFields.value.map(field => {
    if (!isSpaceField(field)) return field;
    return { ...normalizeSpaceField(field), values };
  });
};

watch(
  () =>
    preChatFields.value.map(f => `${f.name}:${f.type}:${f.enabled}`).join(','),
  () => {
    hydrateSpaceFieldValues();
  }
);

watch(
  () => spaces.value.length,
  () => {
    hydrateSpaceFieldValues();
  }
);

const handleUpdateField = (field, key, value) => {
  const target = preChatFields.value.find(f => f.name === field.name);
  if (target) target[key] = value;
};

const handleAddField = field => {
  const normalized = normalizeStandardBookingField({ ...field, enabled: true });
  if (isBookingField(normalized)) {
    normalized.fieldset_id = null;
  }
  const existing = preChatFields.value.find(f => f.name === normalized.name);
  if (existing?.enabled) return;
  if (existing) {
    preChatFields.value = preChatFields.value.map(f =>
      f.name === normalized.name
        ? { ...normalizeStandardBookingField(f), ...normalized, enabled: true }
        : f
    );
  } else {
    preChatFields.value = [...preChatFields.value, normalized];
  }
};

const handleRemoveField = field => {
  if (field.name === 'message') return;
  preChatFields.value = preChatFields.value.map(f =>
    f.name === field.name ? { ...f, enabled: false, fieldset_id: null } : f
  );
};

const handleReorder = updatedFields => {
  const enabledNames = new Set(updatedFields.map(f => f.name));
  const disabledFields = preChatFields.value.filter(
    f => !enabledNames.has(f.name) || !f.enabled
  );
  preChatFields.value = pinMessageFieldLast([
    ...updatedFields,
    ...disabledFields,
  ]);
};

const addFieldset = () => {
  const id = Date.now().toString();
  preChatFieldsets.value = [
    ...preChatFieldsets.value,
    { id, label: '', fields: [] },
  ];
};

const removeFieldset = id => {
  preChatFieldsets.value = preChatFieldsets.value.filter(fs => fs.id !== id);
  preChatFields.value = preChatFields.value.map(f =>
    f.fieldset_id === id ? { ...f, fieldset_id: null } : f
  );
};

const updateFieldsetLabel = (id, label) => {
  preChatFieldsets.value = preChatFieldsets.value.map(fs =>
    fs.id === id ? { ...fs, label } : fs
  );
};

const updateFieldsetIcon = (id, icon) => {
  preChatFieldsets.value = preChatFieldsets.value.map(fs =>
    fs.id === id ? { ...fs, icon } : fs
  );
};

const openCreateAttributeDialog = () => {
  createAttributeDialogRef.value?.open();
};

const onAttributeCreated = () => {
  store.dispatch('attributes/get');
};

const updateInbox = async () => {
  try {
    await hydrateSpaceFieldValues();
    const payload = {
      id: props.inbox.id,
      formData: false,
      channel: {
        pre_chat_form_enabled: preChatFormEnabled.value,
        pre_chat_form_options: {
          pre_chat_message: preChatMessage.value,
          submit_button_label: submitButtonLabel.value,
          required_before_chat: requiredBeforeChat.value,
          pre_chat_fields: pinMessageFieldLast(
            ensureMessageField(preChatFields.value)
          ).map(field =>
            isSpaceField(field)
              ? { ...normalizeSpaceField(field), values: [] }
              : normalizeStandardBookingField(field)
          ),
          pre_chat_fieldsets: preChatFieldsets.value,
        },
      },
    };
    await store.dispatch('inboxes/updateInbox', payload);
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="py-6 px-8 text-base">
    <p class="text-sm text-n-slate-11 mb-4">
      {{ t('INBOX_MGMT.PRE_CHAT_FORM.DESCRIPTION') }}
    </p>

    <!-- Header controls -->
    <div class="flex items-center gap-3 mb-6">
      <label
        class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
      >
        {{ t('INBOX_MGMT.PRE_CHAT_FORM.ENABLE.LABEL') }}
        <ToggleSwitch v-model="preChatFormEnabled" />
      </label>
    </div>

    <template v-if="preChatFormEnabled">
      <div class="flex items-center gap-3 mb-6">
        <label
          class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
        >
          {{ t('INBOX_MGMT.PRE_CHAT_FORM.REQUIRED_BEFORE_CHAT.LABEL') }}
          <ToggleSwitch v-model="requiredBeforeChat" />
        </label>
      </div>
      <p class="text-sm text-n-slate-11 mb-4">
        {{ t('INBOX_MGMT.PRE_CHAT_FORM.REQUIRED_BEFORE_CHAT.HELP') }}
      </p>

      <!-- Pre-chat message -->
      <div class="mb-4 rounded-xl outline outline-1 outline-n-container p-4">
        <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
          {{ t('INBOX_MGMT.PRE_CHAT_FORM.PRE_CHAT_MESSAGE.LABEL') }}
        </label>
        <WootMessageEditor
          :model-value="preChatMessage"
          class="[&_.ProseMirror-menubar]:rounded-tl-[4px] [&_.ProseMirror-menubar]:!relative px-3"
          :placeholder="
            t('INBOX_MGMT.PRE_CHAT_FORM.PRE_CHAT_MESSAGE.PLACEHOLDER')
          "
          @update:model-value="preChatMessage = $event"
        />
      </div>

      <!-- Two-panel builder -->
      <div class="mb-4">
        <div
          class="flex h-[680px] rounded-xl outline outline-1 outline-n-container overflow-hidden"
        >
          <FieldPicker
            :pre-chat-fields="preChatFields"
            @add-field="handleAddField"
            @create-attribute="openCreateAttributeDialog"
          />
          <FormCanvas
            :pre-chat-fields="preChatFields"
            :fieldsets="preChatFieldsets"
            :submit-button-label="submitButtonLabel"
            @update-field="handleUpdateField"
            @reorder="handleReorder"
            @add-field="handleAddField"
            @add-fieldset="addFieldset"
            @remove-fieldset="removeFieldset"
            @update-fieldset-label="updateFieldsetLabel"
            @update-fieldset-icon="updateFieldsetIcon"
            @remove-field="handleRemoveField"
            @update-submit-label="submitButtonLabel = $event"
          />
        </div>
      </div>
    </template>

    <!-- Save button -->
    <div class="mt-6">
      <NextButton
        :label="t('INBOX_MGMT.SETTINGS_POPUP.UPDATE_PRE_CHAT_FORM_SETTINGS')"
        :is-loading="uiFlags.isUpdating"
        @click="updateInbox"
      />
    </div>

    <CreateAttributeDialog
      ref="createAttributeDialogRef"
      @created="onAttributeCreated"
    />
  </div>
</template>
